import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

export default function Incentives() {
  const { role, can } = useApp();
  const [rules, setRules] = useState([]);
  const [given, setGiven] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});

  const load = () => {
    api.get("/incentives/rules").then(({ data }) => setRules(data));
    api.get("/incentives").then(({ data }) => setGiven(data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/incentives/rules", { name: f.name, type: f.type || "Custom", amount: Number(f.amount || 0), calc_type: f.calc_type || "Fixed Amount", location: f.location || "All", min_orders: Number(f.min_orders || 0), status: "Active", _user: role });
      toast.success("Incentive rule created"); setOpen(false); setF({}); load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <PageHeader title="Incentive Engine" subtitle="Configurable incentive rules & credited incentives">
        {can("incentive.add") && <Button onClick={() => setOpen(true)} data-testid="add-incentive-rule-btn"><Plus className="mr-1 h-4 w-4" /> New Rule</Button>}
      </PageHeader>

      <Tabs defaultValue="rules">
        <TabsList><TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger><TabsTrigger value="credited" data-testid="tab-credited">Credited</TabsTrigger></TabsList>
        <TabsContent value="rules" className="mt-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rules.map((r) => (
              <div key={r.id} className="rounded-xl border border-border bg-card p-4" data-testid={`incentive-rule-${r.id}`}>
                <div className="flex items-center justify-between"><h3 className="font-head font-semibold">{r.name}</h3><StatusBadge status={r.status} /></div>
                <p className="mt-1 text-xs text-muted-foreground">{r.type} · {r.calc_type}</p>
                <p className="mt-3 font-head text-2xl font-bold tabular text-amber-500">{inr(r.amount)}</p>
                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                  <p>Location: {r.location}</p><p>Min orders: {r.min_orders}</p>
                  <p>{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="credited" className="mt-4">
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>DP</TableHead><TableHead>Incentive</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{given.map((g) => (
                <TableRow key={g.id}><TableCell className="font-semibold">{g.dp_name}</TableCell><TableCell>{g.name}</TableCell><TableCell>{g.type}</TableCell><TableCell className="text-right tabular font-semibold text-amber-500">{inr(g.amount)}</TableCell><TableCell>{fmtDate(g.date)}</TableCell><TableCell><StatusBadge status={g.status} /></TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card" data-testid="incentive-rule-dialog">
          <DialogHeader><DialogTitle className="font-head">New Incentive Rule</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div><Label>Type</Label><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger><SelectContent>{["Daily", "Weekly", "Monthly", "Rain", "Peak-hour", "Attendance", "Target", "Special", "Festival", "Performance", "Custom"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Calc Type</Label><Select value={f.calc_type} onValueChange={(v) => setF({ ...f, calc_type: v })}><SelectTrigger><SelectValue placeholder="Fixed Amount" /></SelectTrigger><SelectContent>{["Fixed Amount", "Per Order", "Per KM", "Percentage", "Slab Based"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Amount (₹)</Label><Input type="number" value={f.amount || ""} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
            <div><Label>Min Orders</Label><Input type="number" value={f.min_orders || ""} onChange={(e) => setF({ ...f, min_orders: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-incentive-rule">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
